import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaInstagram, FaPlay, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const videos = [
  {
    title: "Modern Design",
    src: "https://www.instagram.com/reel/DYj0jT3gaSt/embed/",
    icon: <FaInstagram color="#E4405F" />,
  },
  {
    title: "Process Reel",
    src: "https://www.instagram.com/reel/DXzAaJUgZ3a/embed/",
    icon: <FaInstagram color="#E4405F" />,
  },
  {
    title: "Premium Finish",
    src: "https://www.instagram.com/reel/DX6uumFAYzY/embed/",
    icon: <FaInstagram color="#E4405F" />,
  },
];

export default function VideoShowcase() {
  const [activeVideo, setActiveVideo] = useState(null);

  // Push history state when modal opens
  useEffect(() => {
    if (activeVideo) {
      if (!window.history.state?.videoModalOpen) {
        window.history.pushState({ videoModalOpen: true }, "");
      }
    }
  }, [activeVideo]);

  // Intercept back button to close the modal
  useEffect(() => {
    const handlePopState = () => {
      if (activeVideo) {
        setActiveVideo(null);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activeVideo]);

  const closeModal = () => {
    if (window.history.state?.videoModalOpen) {
      window.history.back();
    } else {
      setActiveVideo(null);
    }
  };

  const currentIndex = activeVideo ? videos.findIndex((v) => v.src === activeVideo.src) : -1;

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentIndex !== -1) {
      const prevIndex = (currentIndex - 1 + videos.length) % videos.length;
      setActiveVideo(videos[prevIndex]);
    }
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentIndex !== -1) {
      const nextIndex = (currentIndex + 1) % videos.length;
      setActiveVideo(videos[nextIndex]);
    }
  };

  return (
    <section
      className="py-5"
      style={{
        background: "#070707",
        color: "white",
        overflow: "hidden",
      }}
    >
      <div className="container py-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-5"
        >
          <h2
            style={{
              fontSize: "calc(1.6rem + 1.2vw)",
              fontWeight: "800",
              marginBottom: "12px",
            }}
          >
            Experience <span style={{ color: "#F97316" }}>Quality</span>
          </h2>

          <p
            style={{
              color: "#94a3b8",
              maxWidth: "500px",
              margin: "0 auto",
              fontSize: "1rem",
              lineHeight: "1.5",
            }}
          >
            Watch our craftsmanship in action directly on our Instagram feed.
          </p>
        </motion.div>

        {/* Video Reel Cards (Grid Layout) */}
        <div className="row g-4 justify-content-center">
          {videos.map((video, index) => (
            <div
              className="col-12 col-sm-6 col-md-4 d-flex justify-content-center"
              key={index}
            >
              <motion.div
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                onClick={() => setActiveVideo(video)}
                style={{
                  background: "rgba(255, 255, 255, 0.02)",
                  borderRadius: "20px",
                  padding: "10px",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(12px)",
                  width: "100%",
                  maxWidth: "280px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                  cursor: "pointer",
                }}
              >
                {/* Card Header */}
                <div className="d-flex align-items-center gap-2 mb-2 px-1 pt-1">
                  <div style={{ fontSize: "1.1rem", display: "flex" }}>{video.icon}</div>
                  <span
                    style={{
                      fontWeight: "700",
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                      color: "#e2e8f0",
                    }}
                  >
                    {video.title}
                  </span>
                </div>

                {/* Video Container with Play Hover Effect */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "9/16",
                    borderRadius: "14px",
                    overflow: "hidden",
                    background: "#121212",
                  }}
                >
                  <iframe
                    src={video.src}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen
                    title={video.title}
                    style={{
                      border: "none",
                      borderRadius: "14px",
                      display: "block",
                      pointerEvents: "none",
                    }}
                  />
                  
                  {/* Overlay for Play Icon & Click Capture */}
                  <motion.div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0, 0, 0, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background 0.3s ease",
                    }}
                    whileHover={{ background: "rgba(0, 0, 0, 0.4)" }}
                  >
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileHover={{ scale: 1.1, opacity: 1 }}
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background: "#F97316",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 15px rgba(249, 115, 22, 0.4)",
                        color: "#fff",
                      }}
                    >
                      <FaPlay size={16} style={{ marginLeft: "2px" }} />
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>

        {/* Navigation Action */}
        <motion.div className="text-center mt-5" whileHover={{ scale: 1.03 }}>
          <a
            href="https://www.instagram.com/sree_raghavendra_doors"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              color: "white",
              border: "2px solid #F97316",
              padding: "10px 28px",
              borderRadius: "50px",
              fontWeight: "700",
              fontSize: "0.9rem",
              display: "inline-block",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(249, 115, 22, 0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            View All Reels
          </a>
        </motion.div>

        {/* Medium Screen Modal Lightbox */}
        <AnimatePresence>
          {activeVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                backdropFilter: "blur(8px)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <style>{`
                .v-modal-header {
                  display: none !important;
                }
                .mobile-header-title {
                  display: none !important;
                }
                .desktop-header-title {
                  display: flex !important;
                }
                .desktop-close-dots {
                  display: flex !important;
                }
                .mobile-close-btn {
                  display: none !important;
                }

                .v-modal-window {
                  background: #121212;
                  border-radius: 24px;
                  border: 1px solid rgba(255, 255, 255, 0.1);
                  width: min(calc((90vh - 40px) * 9 / 16 + 32px), 480px, 90vw);
                  position: relative;
                  padding: 16px;
                  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
                }

                .v-modal-arrow {
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  width: 44px;
                  height: 44px;
                  border-radius: 50%;
                  background: rgba(255, 255, 255, 0.1);
                  border: 1px solid rgba(255, 255, 255, 0.15);
                  color: white;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  z-index: 1060;
                  transition: all 0.2s ease;
                  backdrop-filter: blur(4px);
                  -webkit-backdrop-filter: blur(4px);
                }
                .v-modal-arrow:hover {
                  background: #F97316;
                  border-color: #F97316;
                  transform: translateY(-50%) scale(1.1);
                  box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
                }
                .v-modal-arrow.left {
                  left: -60px;
                }
                .v-modal-arrow.right {
                  right: -60px;
                }

                .v-modal-close-btn-global {
                  position: absolute;
                  top: 0px;
                  right: -45px;
                  width: 38px;
                  height: 38px;
                  border-radius: 50%;
                  background: rgba(255, 255, 255, 0.15);
                  border: 1px solid rgba(255, 255, 255, 0.25);
                  color: white;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                  z-index: 1070;
                  transition: all 0.2s ease;
                  backdrop-filter: blur(4px);
                  -webkit-backdrop-filter: blur(4px);
                }
                .v-modal-close-btn-global:hover {
                  background: #F97316;
                  border-color: #F97316;
                  box-shadow: 0 4px 15px rgba(249, 115, 22, 0.4);
                }

                @media (max-width: 768px) {
                  .v-modal-window {
                    width: min(calc((90vh - 40px) * 9 / 16 + 32px), 480px, 80vw);
                  }
                  .v-modal-header {
                    display: flex !important;
                  }
                  .mobile-header-title {
                    display: flex !important;
                  }
                  .desktop-header-title {
                    display: none !important;
                  }
                  .v-modal-arrow {
                    top: 55%;
                    width: 38px;
                    height: 38px;
                    background: rgba(0, 0, 0, 0.6);
                    border-color: rgba(255, 255, 255, 0.2);
                  }
                  .v-modal-arrow.left {
                    left: -32px;
                  }
                  .v-modal-arrow.right {
                    right: -32px;
                  }
                  .mobile-close-btn {
                    display: flex !important;
                  }
                  .desktop-close-dots {
                    display: none !important;
                  }
                  .v-modal-close-btn-global {
                    display: none !important;
                  }
                }
              `}</style>

              {/* Modal Window */}
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="v-modal-window"
              >
                {/* Global Close Button (Laptop only) */}
                <motion.button
                  onClick={closeModal}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="v-modal-close-btn-global"
                  title="Close"
                >
                  <FaTimes size={18} />
                </motion.button>

                {/* Modal Header */}
                <div className="v-modal-header d-flex justify-content-between align-items-center mb-3 px-1">
                  
                  {/* Left Side: macOS dots on desktop, title & icon on mobile */}
                  <div className="d-flex align-items-center gap-2">
                    {/* Desktop macOS style window symbols */}
                    <div className="desktop-close-dots align-items-center gap-2">
                      {/* Active Close Button (Red) */}
                      <motion.button
                        onClick={closeModal}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        style={{
                          background: "#FF5F56",
                          border: "none",
                          borderRadius: "50%",
                          width: "13px",
                          height: "13px",
                          cursor: "pointer",
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "rgba(0, 0, 0, 0.6)",
                          fontSize: "8px",
                        }}
                        title="Close"
                      >
                        {/* Optional micro cross inside the red dot on hover */}
                        <motion.span 
                          initial={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          style={{ display: "flex", fontWeight: "bold" }}
                        >
                          <FaTimes size={7} />
                        </motion.span>
                      </motion.button>
                      
                      {/* Decorative Yellow Symbol */}
                      <div
                        style={{
                          background: "#FFBD2E",
                          borderRadius: "50%",
                          width: "13px",
                          height: "13px",
                          opacity: 0.85,
                        }}
                      />
                      
                      {/* Decorative Green Symbol */}
                      <div
                        style={{
                          background: "#27C93F",
                          borderRadius: "50%",
                          width: "13px",
                          height: "13px",
                          opacity: 0.85,
                        }}
                      />
                    </div>

                    {/* Mobile Title & Icon */}
                    <div className="mobile-header-title align-items-center gap-2">
                      <span style={{ fontWeight: "700", color: "#e2e8f0", fontSize: "0.95rem" }}>
                        {activeVideo.title}
                      </span>
                      <div style={{ display: "flex", fontSize: "1rem" }}>
                        {activeVideo.icon}
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Title & Icon on desktop, Close button on mobile */}
                  <div className="d-flex align-items-center gap-2">
                    {/* Desktop Title & Icon */}
                    <div className="desktop-header-title align-items-center gap-2">
                      <span style={{ fontWeight: "700", color: "#e2e8f0", fontSize: "0.95rem" }}>
                        {activeVideo.title}
                      </span>
                      <div style={{ display: "flex", fontSize: "1rem" }}>
                        {activeVideo.icon}
                      </div>
                    </div>

                    {/* Mobile Close Button */}
                    <motion.button
                      onClick={closeModal}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="mobile-close-btn"
                      style={{
                        background: "rgba(255, 255, 255, 0.1)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        padding: 0,
                      }}
                      title="Close"
                    >
                      <FaTimes size={14} />
                    </motion.button>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button className="v-modal-arrow left" onClick={handlePrev} title="Previous Video">
                  <FaChevronLeft size={18} />
                </button>
                <button className="v-modal-arrow right" onClick={handleNext} title="Next Video">
                  <FaChevronRight size={18} />
                </button>

                {/* Larger Video Container */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "9/16",
                    borderRadius: "16px",
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <iframe
                    src={activeVideo.src}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    scrolling="no"
                    allowFullScreen
                    title={activeVideo.title}
                    style={{ border: "none", display: "block" }}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}